import {
  computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { ALLERGY_OPTIONS, ALLERGY_OPTIONS_CN, CURRENT_MEDICINE_TAKEN_OPTIONS, CURRENT_MEDICINE_TAKEN_OPTIONS_CN, MODALITY, PAST_DISEASE_OPTIONS, PAST_DISEASE_OPTIONS_CN, SYMPTOM_OPTIONS, SYMPTOM_OPTIONS_CN, USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM } from '../constants/settings';
import { Gender, PAGE_SIZE } from '../constants/options';
import { ProfileTemplate } from '../utilities/profile';
import { GetApiLang, GetLangKey, GetUserProfile, Language } from '../utilities/general';


export default class HerbStore {

  loading: boolean = false;

  loadingInfo: boolean = false;

  creating: boolean = false;

  updating: boolean = false;

  insertingHerbs: boolean = false;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      loading: observable,
      loadingInfo: observable,
      creating: observable,
      updating: observable,
      insertingHerbs: observable,
    });
    this.rootStore = rootStore;
  }

  searchHerbs = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Herb/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createHerb = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.creating = true;
    try {
      const res = await Axios.post(`/api/Herb?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creating = false;
    }
  };

  updateHerb = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.updating = true;
    try {
      const res = await Axios.put(`/api/Herb?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  };

  removeHerb = async (id: number, lang: Language = GetApiLang()) => {
    this.updating = true;
    try {
      const res = await Axios.delete(`/api/Herb/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  };

  searchHerbFormulas = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Herb/Formula/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createHerbFormula = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.creating = true;
    try {
      const res = await Axios.post(`/api/Herb/Formula?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creating = false;
    }
  };

  updateHerbFormula = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.updating = true;
    try {
      const res = await Axios.put(`/api/Herb/Formula?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  };

  removeHerbFormula = async (id: number, lang: Language = GetApiLang()) => {
    this.updating = true;
    try {
      const res = await Axios.delete(`/api/Herb/Formula/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  };

  getHerbFormula = async (id: number, lang: Language = GetApiLang()) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.get(`/api/Herb/Formula/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  };

  updateFormulaItem = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.insertingHerbs = true;
    try {
      const res = await Axios.put(`/api/Herb/FormulaItem/Update?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.insertingHerbs = false;
    }
  };

  addFormulaItem = async (reqModel: any, lang: Language = GetApiLang()) => {
    this.insertingHerbs = true;
    try {
      const res = await Axios.post(`/api/Herb/FormulaItem/Add?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.insertingHerbs = false;
    }
  };

  searchHerbRecord = async (reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Herb/Search/Record?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchFormulaRecord = async (reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Herb/Formula/Search/Record?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };
}
